/**
 * C7 — Bridge Letter (CONDITIONAL: only when C2 = Partial). Renders as the 7th
 * Scope card (the shell slots it into ScopeSection), styled like C1–C5: a
 * SubcardHeader with an Open|Done badge + verdict tag, read-only once Done with
 * an Edit button that unlocks the controls. Owns A2/A3/A3.1, C2.1/C2.2, AIP-7.
 *
 * Open|Done is driven by `openCount` (the report's open bridge action items,
 * derive-on-read) so it can't drift from the Scope badge; a clean AIP-7 verdict
 * auto-resolves the bridge (Done) the same way a C2–C5 auto-accept does.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ExpandableSection,
  SpaceBetween,
  Box,
  Button,
  Badge,
  Textarea,
  Alert,
  FormField,
  StatusIndicator,
} from "@cloudscape-design/components";
import type { BridgeEvidence } from "@/core/api/types";
import { BridgeEditModal } from "./BridgeEditModal";
import { CommentThread } from "@/core/ui/CommentThread";
import { SubcardHeader } from "@/core/ui/SubcardHeader";
import { formatET } from "@/core/ui/time";
import { useSavedFlash } from "@/core/ui/useSavedFlash";
import type { Focus } from "@/shell/ReportDetail";
import { getBridgeEvidence, requestBridgeLetterUpload, updateBridgeEvidence } from "./api";

interface Props {
  reportId: string;
  /** Open bridge action items for this report (derive-on-read). Drives Open|Done. */
  openCount?: number;
  /** Parent refresh — re-fetches openItems so the Scope badge + this card's
   *  Open|Done update after an upload / narrative save / mark-unobtainable. */
  onUpdated?: () => void;
  focus?: Focus | null;
}

export function BridgeSection({ reportId, openCount = 0, onUpdated, focus }: Props) {
  const [data, setData] = useState<BridgeEvidence | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [a2, setA2] = useState("");
  const [a31, setA31] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, flashSaved] = useSavedFlash(); // #119: auto-dismisses after ~5s
  const [editing, setEditing] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false); // #C10: verdict/summary popup

  const containerRef = useRef<HTMLDivElement>(null);
  const isFocused = focus?.section === "bridge";
  // #C7: Open|Done from the open-items count (single source of truth), like C1–C6.
  const open = openCount > 0;
  const done = !open;
  // #99 pattern: a Done card is read-only; the controls unlock only after Edit.
  const readOnly = done && !editing;
  const [expanded, setExpanded] = useState(isFocused || open);

  const refresh = useCallback(async () => {
    try {
      const evidence = await getBridgeEvidence(reportId);
      setData(evidence);
      setA2(evidence?.a2_narrative ?? "");
      setA31(evidence?.a31_narrative ?? "");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [reportId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // #109: collapse once the card becomes Done; expand + scroll when focused.
  useEffect(() => {
    if (done) setExpanded(false);
  }, [done]);
  useEffect(() => {
    if (isFocused && containerRef.current) {
      setExpanded(true);
      containerRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isFocused]);

  // Auto-poll while AIP-7 is running (status='requested', no ai_data yet).
  useEffect(() => {
    if (!data) return;
    if (data.status !== "requested" || data.ai_data !== null) return;
    const id = setInterval(() => {
      void refresh();
    }, 5_000);
    return () => clearInterval(id);
  }, [data, refresh]);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await requestBridgeLetterUpload(reportId, file);
      setFile(null);
      await refresh();
      onUpdated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const saveNarratives = async () => {
    setBusy(true);
    setError(null);
    try {
      const next = await updateBridgeEvidence(reportId, { a2_narrative: a2, a31_narrative: a31 });
      setData(next);
      setEditing(false);
      flashSaved();
      onUpdated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const markUnobtainable = async () => {
    setBusy(true);
    try {
      const next = await updateBridgeEvidence(reportId, { mark_unobtainable: true });
      setData(next);
      onUpdated?.();
    } finally {
      setBusy(false);
    }
  };

  const ai = data?.ai_data ?? null;
  const c21 = ai?.c21_covers_fye;
  const c22 = ai?.c22_material_changes;
  const materialChanges = c22 === "material_changes";
  // #125: a bridge spanning > ~6 months needs a documented risk assessment even if
  // it reaches the FY end — flag it and require the A2 narrative before Done.
  const longGap = ai?.long_coverage_gap === true;
  const a2Required = data?.status === "unobtainable" || c21 === "no" || materialChanges || longGap;

  // #84A: spinner only while AIP-7 is actually running.
  const processing = !!data && data.status === "requested" && !data.ai_data;

  // #114: a short verdict tag (shown when Done) reading the AIP-7 outcome.
  const verdictTag = (() => {
    if (!data) return null;
    if (data.status === "unobtainable") return "Unobtainable — documented";
    if (data.status === "received") return "Bridge letter received";
    if (materialChanges) return "Material changes evaluated";
    if (c21 === "no") return "Gap documented";
    if (c21 === "yes") return "Covers the gap — no material changes";
    return "Processed";
  })();

  return (
    <div ref={containerRef} className={isFocused ? "focus-flash" : undefined}>
      <ExpandableSection
        variant="container"
        expanded={expanded}
        onChange={({ detail }) => setExpanded(detail.expanded)}
        header={
          <SubcardHeader
            title="C7 — Bridge letter?"
            actions={
              <>
                {/* #C7: the verdict reads in the HEADER (like C1–C5), not buried in
                    the body — the two AIP-7 calls as explicit badges once a letter
                    is processed; the summary verdictTag covers the no-verdict states
                    (unobtainable / received). */}
                {ai ? (
                  <>
                    <Badge color={c21 === "yes" ? "green" : c21 === "no" ? "red" : "grey"}>
                      {c21 === "yes" ? "Covers FYE" : c21 === "no" ? "Doesn't cover FYE" : "Coverage unclear"}
                    </Badge>
                    <Badge
                      color={c22 === "no_changes" ? "green" : c22 === "material_changes" ? "red" : "grey"}
                    >
                      {c22 === "no_changes"
                        ? "No material changes"
                        : c22 === "material_changes"
                          ? "Material changes"
                          : "Changes unclear"}
                    </Badge>
                  </>
                ) : (
                  done && verdictTag && <Badge color="blue">{verdictTag}</Badge>
                )}
                <Badge color={open ? "red" : "green"}>{open ? "Open" : "Done"}</Badge>
              </>
            }
          />
        }
      >
        <SpaceBetween size="s">
          {error && (
            <Alert type="error" header="Error">
              {error}
            </Alert>
          )}

          {readOnly ? (
            // Done → read-only summary + Edit (mirrors C1–C5). Edit reveals the
            // upload / verdict / narrative controls below.
            <SpaceBetween size="s">
              {ai?.c21_rationale && <Box color="text-body-secondary">{ai.c21_rationale}</Box>}
              {ai?.c22_rationale && <Box color="text-body-secondary">{ai.c22_rationale}</Box>}
              {data?.a2_narrative && (
                <Box>
                  <Box variant="awsui-key-label">Risk assessment (A2)</Box>
                  <Box color="text-body-secondary">{data.a2_narrative}</Box>
                </Box>
              )}
              {data?.a31_narrative && (
                <Box>
                  <Box variant="awsui-key-label">Material-changes evaluation (A3.1)</Box>
                  <Box color="text-body-secondary">{data.a31_narrative}</Box>
                </Box>
              )}
              <SpaceBetween direction="horizontal" size="xs">
                {/* #C10: Edit opens the verdict/summary popup; Replace re-uploads. */}
                <Button iconName="edit" onClick={() => setEditModalOpen(true)}>
                  Edit
                </Button>
                <Button iconName="upload" onClick={() => setEditing(true)}>
                  Replace bridge letter
                </Button>
              </SpaceBetween>
            </SpaceBetween>
          ) : (
            <SpaceBetween size="s">
              {!data || data.status === "not_requested" ? (
                <SpaceBetween size="s">
                  <Box>
                    <Box variant="strong">Add the bridge letter</Box>
                    <Box color="text-body-secondary" variant="small">
                      The SOC 1's coverage period ends before your fiscal year-end, leaving a gap. Ask the
                      vendor for a bridge letter — their written assurance covering that gap — and upload the
                      PDF here. We'll confirm it reaches your year-end and that no material changes were made
                      to the controls in between.
                    </Box>
                  </Box>
                  <FormField label="Bridge letter PDF">
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                      disabled={busy}
                    />
                  </FormField>
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button variant="primary" disabled={!file || busy} loading={busy} onClick={upload}>
                      Upload and process
                    </Button>
                    <Button disabled={busy} onClick={markUnobtainable}>
                      Mark unobtainable
                    </Button>
                  </SpaceBetween>
                </SpaceBetween>
              ) : processing ? (
                <Alert type="info" header="Reading the bridge letter">
                  Upload received. We're checking whether the letter covers your fiscal year-end and confirms
                  no material control changes — this usually takes ~30s and the page refreshes when done.
                </Alert>
              ) : (
                <SpaceBetween size="s">
                  <Box variant="strong">What the letter shows</Box>
                  {ai?.c21_rationale && <Box color="text-body-secondary">{ai.c21_rationale}</Box>}
                  {ai?.c22_rationale && <Box color="text-body-secondary">{ai.c22_rationale}</Box>}
                  {/* #C7: Edit the verdict/summary or replace the letter even while
                      the card is still Open (e.g. material changes pending a
                      narrative) — not only once it's Done. */}
                  {!editing && (
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button iconName="edit" onClick={() => setEditModalOpen(true)}>
                        Edit
                      </Button>
                      <Button iconName="upload" onClick={() => setEditing(true)}>
                        Replace bridge letter
                      </Button>
                    </SpaceBetween>
                  )}
                </SpaceBetween>
              )}

              {materialChanges && (
                <FormField
                  label="Evaluate the material changes (required)"
                  description="The bridge letter discloses material changes — management must document its assessment and whether they're acceptable. The card stays Open until this is filled."
                >
                  <Textarea value={a31} onChange={({ detail }) => setA31(detail.value)} rows={4} disabled={busy} />
                </FormField>
              )}

              {longGap && (
                <Alert type="warning" header="Bridge period longer than 6 months">
                  This bridge letter covers about {ai?.coverage_span_days} days
                  {typeof ai?.coverage_span_days === "number"
                    ? ` (~${Math.round(ai.coverage_span_days / 30)} months)`
                    : ""}
                  . A management representation alone is generally insufficient for a gap this long —
                  document your risk assessment and any additional procedures below before marking this Done.
                </Alert>
              )}

              {a2Required && (
                <FormField
                  label="Document risk assessment, additional procedures, remediation plan, and conclusion (required)"
                  description="Required when the bridge letter can't be obtained, doesn't reach your year-end, spans more than ~6 months, or the material changes aren't acceptable."
                >
                  <Textarea value={a2} onChange={({ detail }) => setA2(detail.value)} rows={6} disabled={busy} />
                </FormField>
              )}

              {(a2Required || materialChanges) && (
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    variant="primary"
                    disabled={busy || (materialChanges && !a31.trim()) || (a2Required && !a2.trim())}
                    loading={busy}
                    onClick={saveNarratives}
                  >
                    Save narratives
                  </Button>
                  {savedAt && !busy && (
                    <StatusIndicator type="success">Saved {formatET(new Date(savedAt))}</StatusIndicator>
                  )}
                </SpaceBetween>
              )}

              {/* #C7 fix: editing a Done card must always offer an action + an
                  exit — a clean letter has no required narrative, so without this
                  Edit was a dead-end (button vanished, nothing to do, no way back). */}
              {editing && (
                <SpaceBetween size="s">
                  <FormField
                    label="Replace bridge letter (optional)"
                    description="Upload a corrected letter to re-check coverage and material changes."
                  >
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                      disabled={busy}
                    />
                  </FormField>
                  <SpaceBetween direction="horizontal" size="xs">
                    {file && (
                      <Button variant="primary" disabled={busy} loading={busy} onClick={upload}>
                        Upload and process
                      </Button>
                    )}
                    <Button disabled={busy} onClick={markUnobtainable}>
                      Mark unobtainable
                    </Button>
                    <Button onClick={() => setEditing(false)} disabled={busy}>
                      Cancel
                    </Button>
                  </SpaceBetween>
                </SpaceBetween>
              )}
            </SpaceBetween>
          )}

          <Box>
            <Box variant="awsui-key-label">Comments</Box>
            <CommentThread kind="bridge" targetId={reportId} onChange={refresh} />
          </Box>
        </SpaceBetween>
      </ExpandableSection>
      <BridgeEditModal
        reportId={reportId}
        data={data}
        visible={editModalOpen}
        onDismiss={() => setEditModalOpen(false)}
        onSaved={() => void refresh()}
      />
    </div>
  );
}
